// CRUD for the top-level `curriculum_libraries` documents (the cards shown on
// the Curriculum Hub). Each library doc carries its display metadata, a URL
// slug, and a layout type. Category/textbook/resource content lives in
// subcollections and is managed by `curriculumService.js`.

import { db, storage } from '../../../firebaseConfig';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { LIBRARIES_COLLECTION, sortByOrder, storageRootForLibrary } from './curriculumService';

function sanitizeFileName(fileName = '') {
  return fileName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 120);
}

function makeStorageToken() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

async function safeDeleteStorage(storagePath) {
  if (!storagePath) return;
  try {
    await deleteObject(ref(storage, storagePath));
  } catch (error) {
    // Ignore missing files to keep deletes resilient.
  }
}

async function uploadLibraryLogo(libraryId, file) {
  const fileName = sanitizeFileName(file.name || 'logo');
  const path = `${storageRootForLibrary(libraryId)}/logo/${makeStorageToken()}_${fileName}`;
  const snap = await uploadBytes(ref(storage, path), file);
  const url = await getDownloadURL(snap.ref);
  return { storagePath: path, url };
}

export const LAYOUT_TYPES = {
  CATEGORY: 'category',
  SIMPLE: 'simple',
};

function librariesCollectionRef() {
  return collection(db, LIBRARIES_COLLECTION);
}

function libraryDocRef(libraryId) {
  return doc(db, LIBRARIES_COLLECTION, libraryId);
}

export function slugify(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

// Build a library descriptor (the shape `createCurriculumService` expects) from
// a raw Firestore library document.
export function toLibraryDescriptor(libraryDoc) {
  if (!libraryDoc) return null;
  return {
    id: libraryDoc.id,
    name: libraryDoc.name || 'Untitled',
    description: libraryDoc.description || '',
    layoutType: libraryDoc.layoutType === LAYOUT_TYPES.CATEGORY ? LAYOUT_TYPES.CATEGORY : LAYOUT_TYPES.SIMPLE,
    slug: libraryDoc.slug || libraryDoc.id,
    builtInKey: libraryDoc.builtInKey || '',
    orderIndex: libraryDoc.orderIndex,
    logoUrl: libraryDoc.logoUrl || '',
    logoStoragePath: libraryDoc.logoStoragePath || '',
    storageRoot: storageRootForLibrary(libraryDoc.id),
  };
}

export async function listLibraries() {
  const snap = await getDocs(librariesCollectionRef());
  const libraries = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return sortByOrder(libraries).map(toLibraryDescriptor);
}

export async function getLibraryBySlug(slug) {
  const target = String(slug || '').trim();
  if (!target) return null;

  // Match on the slug field first; fall back to the raw document id so old
  // bookmarks that used an id still resolve.
  const slugQuery = query(librariesCollectionRef(), where('slug', '==', target));
  const slugSnap = await getDocs(slugQuery);
  if (!slugSnap.empty) {
    const docSnap = slugSnap.docs[0];
    return toLibraryDescriptor({ id: docSnap.id, ...docSnap.data() });
  }

  const byId = await getDoc(libraryDocRef(target));
  if (byId.exists()) {
    return toLibraryDescriptor({ id: byId.id, ...byId.data() });
  }
  return null;
}

async function ensureUniqueSlug(baseSlug) {
  const root = baseSlug || 'library';
  const snap = await getDocs(librariesCollectionRef());
  const used = new Set(snap.docs.map((d) => (d.data().slug || d.id)));

  if (!used.has(root)) return root;
  let i = 2;
  while (used.has(`${root}-${i}`)) i += 1;
  return `${root}-${i}`;
}

export async function createLibrary({
  name,
  description = '',
  layoutType = LAYOUT_TYPES.SIMPLE,
  builtInKey = '',
  logoMode = 'none',
  logoFile = null,
  logoUrl = '',
}) {
  const cleanName = String(name || '').trim();
  if (!cleanName) throw new Error('Please enter a library name.');

  const normalizedLayout =
    layoutType === LAYOUT_TYPES.CATEGORY ? LAYOUT_TYPES.CATEGORY : LAYOUT_TYPES.SIMPLE;

  const snap = await getDocs(librariesCollectionRef());
  const orderIndex = snap.size;
  const slug = await ensureUniqueSlug(slugify(cleanName));

  const libraryRef = doc(librariesCollectionRef());

  let resolvedLogoUrl = '';
  let resolvedLogoStoragePath = '';
  if (logoMode === 'file' && logoFile) {
    const uploaded = await uploadLibraryLogo(libraryRef.id, logoFile);
    resolvedLogoUrl = uploaded.url;
    resolvedLogoStoragePath = uploaded.storagePath;
  } else if (logoMode === 'url') {
    resolvedLogoUrl = String(logoUrl || '').trim();
  }

  await setDoc(libraryRef, {
    name: cleanName,
    description: String(description || '').trim(),
    layoutType: normalizedLayout,
    slug,
    builtInKey: builtInKey || '',
    orderIndex,
    logoUrl: resolvedLogoUrl,
    logoStoragePath: resolvedLogoStoragePath,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Simple libraries hold their textbooks in a single implicit bucket category.
  if (normalizedLayout === LAYOUT_TYPES.SIMPLE) {
    const categoryRef = doc(collection(db, LIBRARIES_COLLECTION, libraryRef.id, 'categories'));
    await setDoc(categoryRef, {
      name: 'Library',
      orderIndex: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  return toLibraryDescriptor({
    id: libraryRef.id,
    name: cleanName,
    description,
    layoutType: normalizedLayout,
    slug,
    builtInKey,
    orderIndex,
    logoUrl: resolvedLogoUrl,
    logoStoragePath: resolvedLogoStoragePath,
  });
}

export async function updateLibrary({ libraryId, name, description, layoutType, logoUpdate }) {
  const patch = { updatedAt: serverTimestamp() };
  if (typeof name === 'string') patch.name = name.trim();
  if (typeof description === 'string') patch.description = description.trim();
  if (layoutType) patch.layoutType = layoutType === LAYOUT_TYPES.CATEGORY ? LAYOUT_TYPES.CATEGORY : LAYOUT_TYPES.SIMPLE;

  if (logoUpdate && logoUpdate.mode && logoUpdate.mode !== 'keep') {
    const existingSnap = await getDoc(libraryDocRef(libraryId));
    const existing = existingSnap.exists() ? existingSnap.data() : {};
    const existingPath = existing.logoStoragePath || '';

    if (logoUpdate.mode === 'file' && logoUpdate.file) {
      const uploaded = await uploadLibraryLogo(libraryId, logoUpdate.file);
      await safeDeleteStorage(existingPath);
      patch.logoUrl = uploaded.url;
      patch.logoStoragePath = uploaded.storagePath;
    } else if (logoUpdate.mode === 'url') {
      await safeDeleteStorage(existingPath);
      patch.logoUrl = String(logoUpdate.url || '').trim();
      patch.logoStoragePath = '';
    } else if (logoUpdate.mode === 'remove') {
      await safeDeleteStorage(existingPath);
      patch.logoUrl = '';
      patch.logoStoragePath = '';
    }
  }

  await updateDoc(libraryDocRef(libraryId), patch);
}

// Persist a new display order for the hub. `libraries` is the current ordered
// list (descriptors); the item at `fromIndex` swaps with its neighbour in the
// given direction, and every library's orderIndex is rewritten to match.
export async function reorderLibraries({ libraries, fromIndex, direction }) {
  const targetIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
  if (fromIndex < 0 || fromIndex >= libraries.length) return;
  if (targetIndex < 0 || targetIndex >= libraries.length) return;

  const reordered = [...libraries];
  [reordered[fromIndex], reordered[targetIndex]] = [reordered[targetIndex], reordered[fromIndex]];

  const batch = writeBatch(db);
  reordered.forEach((library, index) => {
    batch.update(libraryDocRef(library.id), { orderIndex: index, updatedAt: serverTimestamp() });
  });
  await batch.commit();
}

// Recursively remove a library and everything beneath it. Storage objects are
// removed by the per-textbook delete path inside curriculumService when textbook
// deletes run; here we batch-delete the Firestore docs.
export async function deleteLibrary({ libraryId }) {
  const librarySnap = await getDoc(libraryDocRef(libraryId));
  if (librarySnap.exists()) {
    await safeDeleteStorage(librarySnap.data().logoStoragePath);
  }

  const categoriesSnap = await getDocs(collection(db, LIBRARIES_COLLECTION, libraryId, 'categories'));

  for (const categoryDoc of categoriesSnap.docs) {
    const textbooksSnap = await getDocs(
      collection(db, LIBRARIES_COLLECTION, libraryId, 'categories', categoryDoc.id, 'textbooks')
    );
    for (const textbookDoc of textbooksSnap.docs) {
      const resourcesSnap = await getDocs(
        collection(
          db,
          LIBRARIES_COLLECTION,
          libraryId,
          'categories',
          categoryDoc.id,
          'textbooks',
          textbookDoc.id,
          'resources'
        )
      );
      // Delete resources in chunks to stay under the 500-op batch limit.
      let batch = writeBatch(db);
      let ops = 0;
      for (const resourceDoc of resourcesSnap.docs) {
        batch.delete(resourceDoc.ref);
        ops += 1;
        if (ops >= 400) {
          await batch.commit();
          batch = writeBatch(db);
          ops = 0;
        }
      }
      if (ops > 0) await batch.commit();
      await deleteDoc(textbookDoc.ref);
    }
    await deleteDoc(categoryDoc.ref);
  }

  await deleteDoc(libraryDocRef(libraryId));
}

// Lightweight per-library summary for hub cards: category + textbook counts.
// Reads categories and their textbook collections (not resources) so it stays cheap.
export async function getLibrarySummary(library) {
  const categoriesSnap = await getDocs(collection(db, LIBRARIES_COLLECTION, library.id, 'categories'));
  let textbookCount = 0;
  await Promise.all(
    categoriesSnap.docs.map(async (categoryDoc) => {
      const textbooksSnap = await getDocs(
        collection(db, LIBRARIES_COLLECTION, library.id, 'categories', categoryDoc.id, 'textbooks')
      );
      textbookCount += textbooksSnap.size;
    })
  );
  return { categoryCount: categoriesSnap.size, textbookCount };
}
